import { motion } from "motion/react";
import { 
  Camera, 
  Search, 
  Brain, 
  Leaf, 
  BookOpen, 
  Database, 
  Activity, 
  Zap, 
  Smartphone,
  Sparkles,
  ArrowRight
} from "lucide-react";

interface HowItWorksProps {
  onScanClick: () => void;
}

export default function HowItWorks({ onScanClick }: HowItWorksProps) {
  const steps = [
    {
      step: "01",
      title: "Upload a photo.",
      description: "Snap a clear, well-lit photo or upload an existing image from your device of any flower, tree, leaf, or houseplant.",
      icon: <Camera className="w-6 h-6 text-green-600" />,
      badge: "STEP 1",
      glowColor: "rgba(34,197,94,0.05)"
    },
    {
      step: "02",
      title: "A Characteristics Analysis",
      description: "Our identification system analyzes visible plant characteristics, including leaf venation, shape patterns, margins, and flower structures.",
      icon: <Search className="w-6 h-6 text-emerald-600" />,
      badge: "STEP 2",
      glowColor: "rgba(16,185,129,0.05)"
    },
    {
      step: "03",
      title: "Botanical Matching",
      description: "View possible matches and botanical information including taxonomic classifications, common names, and scientific categories.",
      icon: <Leaf className="w-6 h-6 text-teal-600" />,
      badge: "STEP 3",
      glowColor: "rgba(20,184,166,0.05)"
    },
    {
      step: "04",
      title: "Education & Growth Care",
      description: "Explore care guides and related plant resources like water volumes, light preferences, humidity tolerances, and growing tips.",
      icon: <BookOpen className="w-6 h-6 text-indigo-600" />,
      badge: "STEP 4",
      glowColor: "rgba(99,102,241,0.05)"
    }
  ];

  const features = [
    { label: "Plant Identification", icon: <Leaf className="w-4 h-4 text-green-600" /> },
    { label: "Care Guides", icon: <BookOpen className="w-4 h-4 text-emerald-600" /> },
    { label: "Disease Detection", icon: <Activity className="w-4 h-4 text-rose-600" /> },
    { label: "Species Information", icon: <Database className="w-4 h-4 text-teal-600" /> },
    { label: "Instant Results", icon: <Zap className="w-4 h-4 text-amber-600" /> },
    { label: "Mobile Friendly", icon: <Smartphone className="w-4 h-4 text-indigo-600" /> }
  ];

  return (
    <section id="how-it-works" className="py-24 bg-white relative overflow-hidden border-t border-b border-gray-150">
      
      {/* Subtle organic gradient backdrop light */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-gradient-to-br from-green-500/[0.01] to-emerald-400/[0.01] rounded-full blur-[140px] pointer-events-none -z-10" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-20 space-y-4">
          
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-1.5 px-3.5 py-1 bg-green-50 border border-green-200 rounded-full text-green-700 text-xs font-extrabold uppercase tracking-widest shadow-2xs select-none"
          >
            <Sparkles className="w-3.5 h-3.5" /> Factual Walkthrough
          </motion.div>

          {/* Heading */}
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.08 }}
            className="text-3xl sm:text-4xl font-extrabold text-gray-950 tracking-tight leading-[1.12]"
          >
            How AI Plant Identification Works
          </motion.h2>

          {/* Subheading */}
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="text-base sm:text-lg text-gray-550 font-medium max-w-[650px] mx-auto leading-relaxed"
          >
            Identify plants in seconds using advanced image recognition technology.
          </motion.p>
        </div>

        {/* 4 Steps Grid Layout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {steps.map((st, idx) => (
            <motion.div
              key={st.step}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.08 }}
              whileHover={{ y: -5 }}
              className="group bg-white border border-gray-150 rounded-2xl p-6.5 relative overflow-hidden transition-all duration-300 flex flex-col justify-between shadow-xs hover:shadow-md hover:border-green-200"
            >
              {/* Hover Radial Light */}
              <div 
                className="absolute inset-0 bg-radial transition-all duration-500 opacity-0 group-hover:opacity-100 pointer-events-none -z-10"
                style={{
                  backgroundImage: `radial-gradient(circle 120px at 50% 50%, ${st.glowColor}, transparent)`
                }}
              />

              <div className="space-y-6">
                
                {/* Step indicators and visual header */}
                <div className="flex justify-between items-center">
                  <div className="w-11 h-11 rounded-xl bg-gray-50 group-hover:bg-green-50/50 border border-gray-100 group-hover:border-green-150 flex items-center justify-center transition-colors duration-300">
                    {st.icon}
                  </div>
                  <span className="text-3xl font-[950] text-gray-200/50 group-hover:text-green-600/10 font-mono select-none transition-colors duration-300">
                    {st.step}
                  </span>
                </div>

                {/* Info and Content */}
                <div className="space-y-2">
                  <span className="text-[10px] font-extrabold text-green-750 tracking-wider uppercase block">
                    {st.badge}
                  </span>
                  <h3 className="text-lg font-bold text-gray-950 tracking-tight group-hover:text-green-700 transition-colors">
                    {st.title}
                  </h3>
                  <p className="text-sm text-gray-500 font-medium leading-relaxed">
                    {st.description}
                  </p>
                </div>

              </div>

              {/* Bottom design elements */}
              <div className="mt-8 pt-4 border-t border-gray-100/60 flex items-center justify-between text-[11px] font-bold text-gray-400">
                <span className="group-hover:text-green-600 transition-colors">Verified Analysis</span>
                <span className="opacity-0 group-hover:opacity-100 transition-all duration-300 text-green-600">
                  Active
                </span>
              </div>

            </motion.div>
          ))}
        </div>

        {/* Action Button */}
        <div className="flex justify-center mb-20">
          <button
            onClick={onScanClick}
            className="px-6 py-3 bg-green-700 hover:bg-green-800 dark:bg-green-600 dark:hover:bg-green-700 text-white font-extrabold text-sm rounded-xl transition-all flex items-center gap-2 shadow-xs hover:shadow-md cursor-pointer inline-flex"
          >
            Start Identification Scanner <span><ArrowRight className="w-4 h-4" /></span>
          </button>
        </div>

        {/* Optional Features Row Badges */}
        <div className="pt-10 border-t border-gray-150 flex flex-col items-center gap-4">
          <span className="text-xs font-bold uppercase tracking-wider text-gray-400 select-none">
            Verified Product Features
          </span>
          
          <div className="flex flex-wrap justify-center gap-3 max-w-4xl">
            {features.map((feat, idx) => (
              <div 
                key={idx}
                className="flex items-center gap-2 px-4 py-2 bg-gray-55/40 hover:bg-green-50/40 border border-gray-150 hover:border-green-200 rounded-full text-xs font-semibold text-gray-700 transition-all shadow-3xs"
              >
                {feat.icon}
                <span>{feat.label}</span>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}
